/**
 * Explorer agrupa por seriesNumber: si dos SeriesUID comparten el mismo número
 * (p.ej. ingest manda sn=1 a todas), el stack se mezcla en una sola serie.
 * Asigna números únicos por SeriesInstanceUID, preferiendo el SN original.
 *
 * Importante (WADO-URI / manifests sin SeriesUID): NO usar SOP como identidad de
 * serie — eso inventaba N miniaturas negras (una por instancia) hasta que llegaba
 * el DICOM con SeriesUID real. Sin UID, agrupar por seriesNumber del manifest.
 *
 * Mid-load: si un SN tiene exactamente un UID real, los stubs heredan ese UID.
 * Explorer agrupa por SeriesUID (`suid:`) y si no por `sn:` — sin heredar,
 * la misma serie se partía en miniatura vacía + stack real hasta el flush final.
 *
 * @param {object[]} items
 */
export function uniquifySeriesNumbers(items) {
  if (!Array.isArray(items) || items.length === 0) return

  const getRealUid = (it) => {
    const raw =
      it.seriesInstanceUID ||
      (it.series && it.series.seriesInstanceUID) ||
      ''
    const u = String(raw || '').trim()
    if (!u || u.indexOf('fallback:') === 0) return ''
    return u
  }

  const preferredSnOf = (it) => {
    const sn = Number(it && it.series && it.series.seriesNumber)
    return Number.isFinite(sn) && sn > 0 ? sn : null
  }

  // UIDs reales vistos + SN preferido
  const uidPreferred = new Map()
  const uidOrder = []
  items.forEach((it) => {
    if (!it) return
    const uid = getRealUid(it)
    if (!uid) return
    if (!uidPreferred.has(uid)) {
      uidOrder.push(uid)
      uidPreferred.set(uid, preferredSnOf(it))
    }
  })

  // SN → UID cuando ese SN tiene exactamente un UID real (une stubs mid-load)
  const snCounts = new Map()
  uidOrder.forEach((uid) => {
    const sn = uidPreferred.get(uid)
    if (sn == null) return
    snCounts.set(sn, (snCounts.get(sn) || 0) + 1)
  })
  const snToUid = new Map()
  uidOrder.forEach((uid) => {
    const sn = uidPreferred.get(uid)
    if (sn == null) return
    if (snCounts.get(sn) === 1) snToUid.set(sn, uid)
  })

  const groupKeyOf = (it) => {
    const uid = getRealUid(it)
    if (uid) return uid
    const sn = preferredSnOf(it)
    if (sn != null) {
      if (snToUid.has(sn)) return snToUid.get(sn)
      return `sn:${sn}`
    }
    // Huérfano sin UID ni SN: último recurso (no debería pasar en manifests OK)
    return `fallback:${it.sopInstanceUID || ''}`
  }

  const keyOrder = []
  const keyPreferred = new Map()
  items.forEach((it) => {
    if (!it) return
    const key = groupKeyOf(it)
    if (!keyPreferred.has(key)) {
      keyOrder.push(key)
      keyPreferred.set(
        key,
        preferredSnOf(it) != null
          ? preferredSnOf(it)
          : uidPreferred.get(key) || null
      )
    }
  })

  const snOwner = new Map()
  const keyToSn = new Map()
  let nextFree = 1
  const claim = (preferred) => {
    let sn =
      preferred != null && Number.isFinite(preferred) && preferred > 0
        ? preferred
        : nextFree
    while (snOwner.has(sn)) {
      sn = nextFree
      nextFree += 1
    }
    if (sn >= nextFree) nextFree = sn + 1
    snOwner.set(sn, true)
    return sn
  }
  keyOrder.forEach((key) => {
    keyToSn.set(key, claim(keyPreferred.get(key)))
  })
  items.forEach((it) => {
    if (!it) return
    const key = groupKeyOf(it)
    const sn = keyToSn.get(key)
    if (sn == null) return
    if (!it.series) it.series = {}
    it.series.seriesNumber = sn
    const realUid = getRealUid(it)
    const inherited =
      !realUid &&
      key &&
      key.indexOf('sn:') !== 0 &&
      key.indexOf('fallback:') !== 0
        ? key
        : ''
    const uid = realUid || inherited
    if (uid) {
      it.seriesInstanceUID = uid
      it.series.seriesInstanceUID = uid
    } else if (!it.series.seriesInstanceUID && it.seriesInstanceUID) {
      it.series.seriesInstanceUID = it.seriesInstanceUID
    }
  })
}
